import React, { useEffect, useState, useMemo } from 'react';
import TopicCard from '../../components/Course/TopicCard';
import './CourseSidebarMain.css';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const TOPICS_API = process.env.REACT_APP_TOPICS_API || `${API_BASE_URL}/api/admin-app/public-aptitude-topics/`;

const CourseSidebarMain = () => {
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('default');

    // Fetch topics from backend
    useEffect(() => {
        const fetchTopics = async () => {
            try {
                setLoading(true);
                const response = await fetch(TOPICS_API);
                if (!response.ok) {
                    throw new Error(`Failed to fetch topics: ${response.statusText}`);
                }
                const data = await response.json();
                setTopics(data);
                setError('');
            } catch (err) {
                console.error('Error fetching topics:', err);
                setError('Unable to load topics. Please try again later.');
                setTopics([]);
            } finally {
                setLoading(false);
            }
        };

        fetchTopics();
    }, []);

    // Filter and sort topics
    const filteredAndSortedTopics = useMemo(() => {
        let result = [...topics];

        // Filter by search term
        if (searchTerm.trim()) {
            const query = searchTerm.toLowerCase();
            result = result.filter(topic =>
                topic.name.toLowerCase().includes(query)
            );
        }

        // Sort
        switch (sortBy) {
            case 'name_asc':
                result.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case 'name_desc':
                result.sort((a, b) => b.name.localeCompare(a.name));
                break;
            case 'newest':
                result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                break;
            case 'oldest':
                result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                break;
            default:
                // Default: alphabetical
                result.sort((a, b) => a.name.localeCompare(b.name));
        }

        return result;
    }, [topics, searchTerm, sortBy]);

    return (
        <div className="topics-container pt---100 pb---40">
            <div className="container pb---70">
                <div className="topics-header mb---40">
                    <div className="header-content">
                        <h2 className="topics-title">Aptitude Test Topics</h2>
                        <p className="topics-subtitle">
                            Select a topic to test your skills and improve your aptitude
                        </p>
                    </div>
                </div>

                <div className="topics-controls mb---30">
                    <div className="search-box">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="search-icon">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <input
                            type="text"
                            placeholder="Search topics..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="sort-select"
                    >
                        <option value="default">Default Sorting</option>
                        <option value="name_asc">Sort A to Z</option>
                        <option value="name_desc">Sort Z to A</option>
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                    </select>
                </div>

                <div className="results-info mb---20">
                    <span className="result-count">
                        {loading ? 'Loading...' : `Found ${filteredAndSortedTopics.length} topic${filteredAndSortedTopics.length !== 1 ? 's' : ''}`}
                    </span>
                </div>

                {loading ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading topics...</p>
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <p>{error}</p>
                    </div>
                ) : filteredAndSortedTopics.length === 0 ? (
                    <div className="empty-container">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                        </svg>
                        <h3>No topics found</h3>
                        <p>
                            {searchTerm
                                ? 'Try adjusting your search terms'
                                : 'No topics available at this moment. Please check back later.'}
                        </p>
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="clear-search-btn"
                            >
                                Clear Search
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="topics-grid">
                        {filteredAndSortedTopics.map((topic) => (
                            <div key={topic.id} className="topic-grid-item">
                                <TopicCard
                                    topicId={topic.id}
                                    topicName={topic.name}
                                    questionCount={topic.questions_count || 0}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CourseSidebarMain;
